import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Briefcase, Plus, ArrowRight, GripVertical, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const DEAL_STAGES = [
  'Initial Contact',
  'NCD Sent',
  'Interest Shown',
  'Meeting Requested',
  'Meeting Scheduled',
  'Under Negotiation',
  'Closed Won',
  'Closed Lost',
];

const DEAL_TYPES = ['Out-License', 'Fund Raise', 'Partnership', 'Co-Development'];

const STAGE_STYLES = {
  'Initial Contact': { bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-400' },
  'NCD Sent': { bg: 'bg-sky-50', border: 'border-sky-200', dot: 'bg-sky-500' },
  'Interest Shown': { bg: 'bg-violet-50', border: 'border-violet-200', dot: 'bg-violet-500' },
  'Meeting Requested': { bg: 'bg-violet-50', border: 'border-violet-200', dot: 'bg-violet-400' },
  'Meeting Scheduled': { bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500' },
  'Under Negotiation': { bg: 'bg-orange-50', border: 'border-orange-200', dot: 'bg-orange-500' },
  'Closed Won': { bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  'Closed Lost': { bg: 'bg-rose-50', border: 'border-rose-200', dot: 'bg-rose-500' },
};

const emptyForm = {
  title: '', biotech_company_id: '', deal_type: 'Out-License', stage: 'Initial Contact',
  pharma_contact_ids: [], investor_ids: [], description: '', value: '', notes: '',
};

export default function DealPipelinePage() {
  const [deals, setDeals] = useState([]);
  const [biotechCompanies, setBiotechCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [viewMode, setViewMode] = useState('kanban');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchData = async () => {
    try {
      const [dealsRes, biotechRes] = await Promise.all([
        axios.get(`${API}/deals`, { withCredentials: true }),
        axios.get(`${API}/biotech-companies`, { withCredentials: true }),
      ]);
      setDeals(dealsRes.data);
      setBiotechCompanies(biotechRes.data?.items || biotechRes.data || []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/deals/${editing}`, form, { withCredentials: true });
        toast.success('Deal updated');
      } else {
        await axios.post(`${API}/deals`, form, { withCredentials: true });
        toast.success('Deal created');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ ...emptyForm });
      fetchData();
    } catch { toast.error('Failed to save deal'); }
  };

  const handleEdit = (deal) => {
    setForm(deal);
    setEditing(deal.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/deals/${id}`, { withCredentials: true });
      toast.success('Deal deleted');
      setDeleteTarget(null);
      fetchData();
    } catch { toast.error('Failed to delete'); setDeleteTarget(null); }
  };

  const moveStage = async (dealId, newStage) => {
    try {
      await axios.patch(`${API}/deals/${dealId}/stage`, { stage: newStage }, { withCredentials: true });
      toast.success(`Moved to ${newStage}`);
      fetchData();
    } catch { toast.error('Failed to update stage'); }
  };

  const getCompanyName = (id) => biotechCompanies.find(c => c.id === id)?.company_name || '—';

  const dealsByStage = DEAL_STAGES.reduce((acc, stage) => {
    acc[stage] = deals.filter(d => d.stage === stage);
    return acc;
  }, {});

  return (
    <div className="p-6 md:p-8 max-w-full mx-auto" data-testid="deal-pipeline-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Deal Pipeline</h1>
          <p className="text-sm text-slate-500 mt-1">Track deals from initial contact to close</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-100 rounded-md p-0.5">
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${viewMode === 'kanban' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              data-testid="view-kanban-btn"
            >Kanban</button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${viewMode === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              data-testid="view-list-btn"
            >List</button>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditing(null); setForm({ ...emptyForm }); } }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-md shadow-sm active:scale-95 transition-all" data-testid="add-deal-btn">
                <Plus className="w-4 h-4 mr-2" /> New Deal
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editing ? 'Edit Deal' : 'New Deal'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Deal Title *</Label>
                  <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required placeholder="e.g. XYZ Biotech - Oncology Out-License" data-testid="input-deal-title" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Biotech Company</Label>
                    <Select value={form.biotech_company_id} onValueChange={v => setForm(p => ({ ...p, biotech_company_id: v }))}>
                      <SelectTrigger data-testid="select-deal-biotech"><SelectValue placeholder="Select company" /></SelectTrigger>
                      <SelectContent>
                        {biotechCompanies.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Deal Type</Label>
                    <Select value={form.deal_type} onValueChange={v => setForm(p => ({ ...p, deal_type: v }))}>
                      <SelectTrigger data-testid="select-deal-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DEAL_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Stage</Label>
                    <Select value={form.stage} onValueChange={v => setForm(p => ({ ...p, stage: v }))}>
                      <SelectTrigger data-testid="select-deal-stage"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DEAL_STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Estimated Value</Label>
                    <Input value={form.value} onChange={e => setForm(p => ({ ...p, value: e.target.value }))} placeholder="e.g. $5M" data-testid="input-deal-value" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} data-testid="input-deal-description" />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} data-testid="input-deal-notes" />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="submit-deal-btn">{editing ? 'Update' : 'Create Deal'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>
      ) : viewMode === 'kanban' ? (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
          {DEAL_STAGES.map((stage) => {
            const style = STAGE_STYLES[stage];
            return (
              <div key={stage} className="min-w-[260px] w-[260px] shrink-0" data-testid={`kanban-column-${stage.toLowerCase().replace(/ /g, '-')}`}>
                <div className={`rounded-lg border ${style.border} ${style.bg} p-3 mb-3`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${style.dot}`} />
                    <span className="text-sm font-semibold text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{stage}</span>
                    <Badge variant="secondary" className="ml-auto text-xs">{dealsByStage[stage]?.length || 0}</Badge>
                  </div>
                </div>
                <div className="space-y-2">
                  {dealsByStage[stage]?.map((deal) => (
                    <Card key={deal.id} className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer group" data-testid={`deal-card-${deal.id}`}>
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-900 truncate">{deal.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{getCompanyName(deal.biotech_company_id)}</p>
                          </div>
                          <GripVertical className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-[10px]">{deal.deal_type}</Badge>
                          {deal.value && <span className="text-[10px] font-medium text-emerald-600">{deal.value}</span>}
                        </div>
                        <div className="flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={(e) => { e.stopPropagation(); handleEdit(deal); }} data-testid={`edit-deal-${deal.id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={(e) => { e.stopPropagation(); setDeleteTarget(deal); }} data-testid={`delete-deal-${deal.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                          {/* Stage navigation arrows */}
                          {DEAL_STAGES.indexOf(stage) < DEAL_STAGES.length - 1 && (
                            <Button
                              variant="ghost" size="sm" className="h-6 px-1 ml-auto text-sky-600 hover:text-sky-700"
                              onClick={(e) => { e.stopPropagation(); moveStage(deal.id, DEAL_STAGES[DEAL_STAGES.indexOf(stage) + 1]); }}
                              data-testid={`move-deal-${deal.id}`}
                            >
                              <ArrowRight className="w-3 h-3" />
                              <span className="text-[10px] ml-0.5">Next</span>
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {dealsByStage[stage]?.length === 0 && (
                    <div className="text-center py-8 text-xs text-slate-400">No deals</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* List View */
        <Card className="border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="deals-list-table">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Deal</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Company</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Stage</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Value</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {deals.map((d) => {
                  const style = STAGE_STYLES[d.stage] || STAGE_STYLES['Initial Contact'];
                  return (
                    <tr key={d.id} className="border-b hover:bg-slate-50/50" data-testid={`deal-row-${d.id}`}>
                      <td className="px-4 py-3 font-semibold text-slate-900">{d.title}</td>
                      <td className="px-4 py-3 text-slate-600">{getCompanyName(d.biotech_company_id)}</td>
                      <td className="px-4 py-3"><Badge variant="outline" className="text-xs">{d.deal_type}</Badge></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${style.dot}`} />
                          <span className="text-sm">{d.stage}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-emerald-600 font-medium">{d.value || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(d)}><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(d)} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Delete Deal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-semibold text-slate-700">{deleteTarget?.title}</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteTarget?.id)} className="bg-red-600 text-white hover:bg-red-700" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
